"use client";
import { useCallback } from "react";

import { useLocalizedDateFormat } from "../../../../core";
import {
  StaticData,
  useFlightStaticData,
} from "../../../../core/flight-static-data";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { ContentGroup } from "../../../../core/resource/types";

import { getTripFromApiSearchData } from "../../../../mobile";
import { FlightSearchAPIData } from "../../../../mobile/components/FlightSearch/types";
import createAirlines from "../../../../mobile/components/FlightSearch/utils/flight/createAirlines";
import flattenSegments from "../../../../mobile/components/FlightSearch/utils/flight/flattenSegments";
import parseFlightApiDate from "../../../../mobile/components/FlightSearch/utils/flight/parseFlightApiDate";
import { printDuration } from "../../../../mobile/components/FlightSearch/utils/flight/printDuration";
import { FlightInfoBaseComponentProps } from "../types";

export function useTransformFlightSearchIntoFlightInfos() {
  const staticData = useFlightStaticData();
  const { format: formatter } = useLocalizedDateFormat();
  const { CorporateEnumSeatClass: cre, CorporateFlightGeneral: cr } =
    useMobileContentResource();

  const createSummary = useCallback(
    (
      type: "DEPART" | "RETURN",
      journeys: FlightSearchAPIData["journeys"]
    ): FlightInfoBaseComponentProps => {
      if (staticData.isError || staticData.isLoading) {
        return {} as FlightInfoBaseComponentProps;
      }
      const combineContentResource = { ...cre, ...cr };
      return {
        type: type,
        info: getInfoFromApiSearchData(
          journeys,
          staticData.data,
          combineContentResource,
          formatter
        ),
        trip: getTripFromApiSearchData(
          journeys,
          staticData.data,
          combineContentResource,
          formatter
        ),
      };
    },
    [staticData, cr, cre, formatter]
  );

  return useCallback(
    (data: FlightSearchAPIData[]): FlightInfoBaseComponentProps[] => {
      const [departTrip, returnTrip] = data;
      const summaries: FlightInfoBaseComponentProps[] = [
        createSummary("DEPART", departTrip.journeys),
      ];
      if (returnTrip)
        summaries.push(createSummary("RETURN", returnTrip.journeys));
      return summaries;
    },
    [createSummary]
  );
}

function getInfoFromApiSearchData(
  journeys: FlightSearchAPIData["journeys"],
  staticData: StaticData,
  cr: ContentGroup,
  formatter: ReturnType<typeof useLocalizedDateFormat>["format"]
): FlightInfoBaseComponentProps["info"] {
  const segments = flattenSegments(journeys);
  const departureSegment = segments[0];
  const arrivalSegment = segments[segments.length - 1];

  const airlines = createAirlines(segments, staticData, cr);

  const departureDateTime = parseFlightApiDate(
    departureSegment.departureDetail.departureDate,
    departureSegment.departureDetail.departureTime
  );

  const duration = journeys.reduce(
    (acc, cur) => acc + Number(cur.journeyDuration),
    0
  );

  return {
    airlineLogos: airlines.map((a) => a.image),
    departureDetail: {
      airportCode: departureSegment.departureDetail.airportCode,
      departureDate: formatter(
        new Date(departureSegment.departureDetail.departureDate),
        "SHORT_WEEKDAY_2"
      ),
      departureTime: departureSegment.departureDetail.departureTime,
    },
    arrivalDetail: {
      airportCode: arrivalSegment.arrivalDetail.airportCode,
      arrivalDate: formatter(
        new Date(arrivalSegment.arrivalDetail.arrivalDate),
        "SHORT_WEEKDAY_2"
      ),
      arrivalTime: arrivalSegment.arrivalDetail.arrivalTime,
    },
    duration: printDuration(duration, cr),
    numOfTransit:
      segments.length + segments.filter((s) => s.stopInfo).length - 1,
  };
}
